import { fabric } from 'fabric';

type Grapheme = string;
type GraphemeLine = Grapheme[];

type PatchedFabric = typeof fabric & { __hasPatchedTextboxWrap?: boolean };

/**
 * Patches `fabric.Textbox.prototype._wrapLine` to add CSS-like
 * `overflow-wrap: break-word` behavior:
 *  1. Delegate to Fabric's original space-based wrap.
 *  2. If any produced line is still wider than the target width
 *     (i.e. contains a single unbroken word longer than the textbox),
 *     that line is re-wrapped by grapheme so glyphs no longer overflow
 *     the textbox / card canvas.
 *
 * Text that already contains natural break points (spaces) keeps its
 * current behavior. Only the overflowing long-word case changes.
 *
 * Idempotent: safe to call multiple times.
 * Fabric version: 5.5.2 (see _wrapLine in fabric.js).
 */
export const patchFabricTextboxWrap = (): void => {
  const patchedFabric = fabric as PatchedFabric;
  if (patchedFabric.__hasPatchedTextboxWrap) {
    return;
  }

  const prototype = fabric.Textbox.prototype as unknown as {
    _wrapLine: (line: string, lineIndex: number, desiredWidth: number, reservedSpace?: number) => GraphemeLine[];
    _measureWord: (word: GraphemeLine, lineIndex: number, charOffset: number) => number;
    _getWidthOfCharSpacing: () => number;
    dynamicMinWidth: number;
  };

  const originalWrapLine = prototype._wrapLine;

  prototype._wrapLine = function patchedWrapLine(
    line: string,
    lineIndex: number,
    desiredWidth: number,
    reservedSpace?: number,
  ): GraphemeLine[] {
    const wrapped = originalWrapLine.call(this, line, lineIndex, desiredWidth, reservedSpace);
    const maxWidth = desiredWidth - (reservedSpace || 0);
    if (maxWidth <= 0) {
      return wrapped;
    }

    const additionalSpace = this._getWidthOfCharSpacing();
    const fixed: GraphemeLine[] = [];
    let needsBreakWord = false;

    for (const wrappedLine of wrapped) {
      const lineWidth = this._measureWord(wrappedLine, lineIndex, 0);
      if (lineWidth <= maxWidth) {
        fixed.push(wrappedLine);
        continue;
      }

      needsBreakWord = true;
      let current: GraphemeLine = [];
      let currentWidth = 0;
      for (const grapheme of wrappedLine) {
        const graphemeWidth = this._measureWord([grapheme], lineIndex, 0);
        const projected = currentWidth + graphemeWidth + (current.length > 0 ? additionalSpace : 0);
        if (projected > maxWidth && current.length > 0) {
          fixed.push(current);
          current = [grapheme];
          currentWidth = graphemeWidth;
        } else {
          current.push(grapheme);
          currentWidth = projected;
        }
      }
      if (current.length > 0) {
        fixed.push(current);
      }
    }

    // Fabric's original _wrapLine sets dynamicMinWidth to the width of the
    // largest single word, and initDimensions() auto-grows the textbox width
    // up to that value. When we break a long word by grapheme the textbox no
    // longer needs that lower bound — clamp it so the textbox does not grow
    // past its authored width (which would push the dashed border past the
    // card canvas, PRCM-10898).
    if (needsBreakWord && this.dynamicMinWidth > desiredWidth) {
      this.dynamicMinWidth = desiredWidth;
    }

    return fixed;
  };

  patchedFabric.__hasPatchedTextboxWrap = true;
};
